import { Component, Prop, h } from '@stencil/core';
import { getConstant } from '../../../../../utils/constants-helpers';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_CLASS = 'sn-related-search-terms-type-1';

/**
 * Related search terms component for SignNow brand search suggestions.
 * Flat markup: no nested me-section, me-container, me-heading, me-tag.
 *
 * @product signnow
 * @source rst
 * @version 1.0.2
 * @category search-terms
 * @contentConfig Related search terms component for displaying related search suggestions
 * @constant RELATED_SEARCH_TERMS_HEADING - "Related searches to office signature feature" - Main heading for related search terms section
 */
@Component({
  tag: 'sn-related-search-terms-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNRelatedSearchTermsType1 {
  /**
   * Component constants object containing all required text constants
   */
  @Prop({ mutable: true }) constants?: Record<string, string> | string;

  /**
   * HTML tag for the heading element (h1, h2, h3, h4, h5, h6)
   * @default "h3"
   */
  @Prop() headingTag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' = 'h3';

  /**
   * Array of search terms (strings) to display.
   * @dynamic
   * @type {array}
   * @.type {object}
   * @.property {string} label - Search term label text
   * @.property {string} href - Optional link URL (auto-generated from label if not provided)
   * @.required label
   * @minItems 1
   */
  @Prop({ mutable: true }) items!: any[] | string;

  componentWillLoad() {
    this.constants = parseJsonProp(this.constants);
    this.items = parseJsonProp(this.items, []);
  }

  render() {
    const items = Array.isArray(this.items) ? this.items : [];
    if (items.length === 0) {
      return null;
    }

    const HeadingTag = this.headingTag;
    const headingText = getConstant(
      this.constants,
      'RELATED_SEARCH_TERMS_HEADING',
      'Related searches to office signature feature'
    );

    return (
      <section class={COMPONENT_CLASS} aria-label="Related Search Terms">
        <div class={`${COMPONENT_CLASS}__container`}>
          <header class={`${COMPONENT_CLASS}__header`}>
            <HeadingTag class={`${COMPONENT_CLASS}__heading`}>
              {headingText}
            </HeadingTag>
          </header>
          <div
            class={`${COMPONENT_CLASS}__list`}
            itemScope
            itemType="https://schema.org/ItemList"
          >
            {items.map((label, index) => (
              <div
                key={index}
                class={`${COMPONENT_CLASS}__item`}
                itemScope
                itemType="https://schema.org/ListItem"
                itemProp="itemListElement"
              >
                <span class={`${COMPONENT_CLASS}__tag`} itemProp="name">
                  {label}
                </span>
                <meta itemProp="position" content={String(index + 1)} />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }
}
